/**
 * Crash Context Collector
 *
 * Gathers sanitized crash context from app stores when an error occurs.
 *
 * PRIVACY PRINCIPLE: This module NEVER collects:
 * - Database credentials or connection strings
 * - Query results or row data
 * - Full SQL queries (only "query executed" action type)
 * - User file paths beyond app data directory
 * - Stack traces containing user paths
 */

import type {
  CrashContext,
  WorkspaceSnapshot,
  ConnectionSnapshot,
  AppInfo,
  RuntimeMetrics,
  SanitizedError,
  ErrorCategory,
  EngineType,
  PlatformType,
} from '../types/crashTypes';
import { getBreadcrumbs } from './breadcrumbService';
import { redactSecretText } from '../../../types/safeConnectionDisplay';

const KNOWN_ENGINES: readonly EngineType[] = [
  'mssql',
  'pgsql',
  'sqlite',
  'mysql',
  'oracle',
  'kusto',
];

function asKnownEngine(engine: string | undefined): EngineType | null {
  return engine && (KNOWN_ENGINES as readonly string[]).includes(engine)
    ? (engine as EngineType)
    : null;
}

// =============================================================================
// App Start Time (for sessionAge calculation)
// =============================================================================

const APP_START_TIME = Date.now();

// =============================================================================
// App Info (set at startup for sync access)
// =============================================================================

let APP_VERSION = 'unknown';
let APP_ARCH = 'unknown';

/**
 * Set the app version for synchronous access in crash context.
 * Should be called at app startup once the version is known.
 */
export function setAppVersion(version: string): void {
  APP_VERSION = version;
}

/**
 * Set the app architecture for synchronous access in crash context.
 * Should be called at app startup.
 */
export function setAppArch(arch: string): void {
  APP_ARCH = arch;
}

// =============================================================================
// Store Accessors (set at startup to avoid dynamic require issues)
// =============================================================================

type WorkspaceStoreAccessor = () => {
  tabs: Array<{
    id: string;
    type: string;
    connectionInfo?: { connectionId?: string };
  }>;
  activeTabId: string | null;
};

type ConnectionStoreAccessor = () => {
  activeConnections: Array<{
    id: string;
    connection?: { engine?: string };
  }>;
};

let workspaceStoreAccessor: WorkspaceStoreAccessor | null = null;
let connectionStoreAccessor: ConnectionStoreAccessor | null = null;

/**
 * Register store accessors for crash context collection.
 * Must be called at app startup before any crashes can be properly reported.
 */
export function registerStoreAccessors(
  workspace: WorkspaceStoreAccessor,
  connection: ConnectionStoreAccessor
): void {
  workspaceStoreAccessor = workspace;
  connectionStoreAccessor = connection;
}

// =============================================================================
// Error Category Mapping
// =============================================================================

/**
 * Map an error to a safe, generic category.
 * Never exposes error details that could contain sensitive information.
 */
export function categorizeError(error: unknown): ErrorCategory {
  if (error instanceof RangeError) return 'range_error';
  if (error instanceof TypeError) return 'type_error';
  if (error instanceof ReferenceError) return 'reference_error';
  if (error instanceof SyntaxError) return 'syntax_error';

  if (error instanceof Error) {
    const msg = error.message.toLowerCase();

    // Memory-related errors
    if (
      msg.includes('out of memory') ||
      msg.includes('allocation failed') ||
      msg.includes('heap')
    ) {
      return 'memory';
    }

    // Timeout errors
    if (msg.includes('timeout') || msg.includes('timed out')) {
      return 'timeout';
    }

    // Network errors
    if (
      msg.includes('network') ||
      msg.includes('fetch') ||
      msg.includes('connection refused') ||
      msg.includes('econnrefused') ||
      msg.includes('enotfound')
    ) {
      return 'network';
    }

    // Generic Error instance - categorize as 'error' rather than 'unknown'
    return 'error';
  }

  // Non-Error types (strings, numbers, etc.) remain 'unknown'
  return 'unknown';
}

/**
 * Extract a single-line, privacy-safe location hint from an Error's stack.
 * Parses the first user-code frame and returns `"at fnName [basename:line:col]"`.
 * - Strips absolute paths and query strings, keeping only the source basename.
 * - Omits the leading `/` in the basename wrapper so path-sanitization regexes
 *   don't rewrite it.
 * - Returns `''` when no usable frame is found.
 */
export function extractSanitizedStackFrame(error: unknown): string {
  if (!(error instanceof Error) || !error.stack) return '';

  const lines = error.stack.split('\n');
  // Skip the "Error: message" header. Find the first "at ..." frame.
  for (const raw of lines) {
    const line = raw.trim();
    if (!line.startsWith('at ')) continue;

    // Supported shapes:
    //   at fnName (file:///abs/path/to/Foo.tsx:12:34)
    //   at fnName (http://host/assets/Foo.tsx:12:34)
    //   at file:///abs/path/to/Foo.tsx:12:34
    //   at Foo.tsx:12:34
    //   at async fnName (file:///...:12:34)
    const withParens = line.match(/^at\s+(.+?)\s+\((.+):(\d+):(\d+)\)$/);
    const bare = line.match(/^at\s+(.+):(\d+):(\d+)$/);

    let fnName = '';
    let loc = '';
    if (withParens) {
      fnName = withParens[1];
      loc = withParens[2];
    } else if (bare) {
      loc = bare[1];
    } else {
      continue;
    }

    // Strip query strings / source-map hashes, then take basename only.
    loc = loc.split('?')[0];
    const basename = loc.split(/[/\\]/).pop() ?? loc;

    // Never report a frame inside the crash-reporting pipeline itself — a
    // synthesized Error's top frames land here and the "location" would point
    // at the reporter instead of the crash. Path-based, so it only works on
    // unminified (dev) stacks; synthesized errors ALSO get their stack cleared
    // at the synthesis site (errorSerialization.ts) to cover prod bundles.
    if (loc.includes('crash-dialog')) continue;

    // Drop leading "async " marker some engines emit in fnName.
    fnName = fnName.replace(/^async\s+/, '');

    const lineCol = withParens ? `${withParens[3]}:${withParens[4]}` : `${bare![2]}:${bare![3]}`;
    const locPart = `[${basename}:${lineCol}]`;
    return fnName ? `at ${fnName} ${locPart}` : `at ${locPart}`;
  }
  return '';
}

/**
 * Sanitize an error message to remove potentially sensitive information.
 * Strips file paths, connection details, and user data.
 * Also appends a privacy-safe stack frame hint when one is available.
 */
export function sanitizeErrorMessage(error: unknown): string {
  if (!(error instanceof Error)) {
    return 'Unknown error occurred';
  }

  // Credentials first (security slice 2026-09-28): the path / connection patterns below would split a SAS URL, a
  // descriptor or `MY_WALLET_PASSWORD = …` and leave the secret behind.
  let message = redactSecretText(error.message);

  // The browser emits the literal string "Script error." for uncaught errors
  // that originate in cross-origin scripts (details censored for security).
  // Replace with an actionable hint pointing the user at DevTools.
  if (message === 'Script error.') {
    message = 'Uncaught error (details censored by browser — open DevTools Console for the real error)';
  }

  // Remove file paths (Unix and Windows)
  message = message.replace(/\/[\w\-./]+/g, '[path]');
  message = message.replace(/[A-Z]:\\[\w\-\\./]+/gi, '[path]');

  // Remove potential connection strings
  message = message.replace(
    /(?:Server|Host|Data Source)=[\w\-.:]+/gi,
    '[connection]'
  );
  message = message.replace(
    /(?:User Id|Username|Login)=[\w\-@.]+/gi,
    '[user]'
  );

  // Remove passwords (critical - never expose credentials)
  message = message.replace(
    /(?:Password|Pwd|Secret|Token|ApiKey|Api_Key)=[\S]+/gi,
    '[credential]'
  );

  // Remove IP addresses
  message = message.replace(/\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/g, '[ip]');

  // Remove port numbers in context
  message = message.replace(/:\d{4,5}/g, ':[port]');

  // Remove email-like patterns
  message = message.replace(/[\w.-]+@[\w.-]+\.\w+/g, '[email]');

  // Append a sanitized stack frame hint AFTER the regex sanitization passes so
  // that line numbers inside the frame aren't rewritten as `[port]`. The frame
  // is extracted into a bracketed form that contains no paths/IPs/credentials,
  // so it doesn't need to run through the regex pipeline.
  if (message) {
    const frame = extractSanitizedStackFrame(error);
    if (frame) {
      message = `${message} — ${frame}`;
    }
  }

  // Truncate long messages
  if (message.length > 200) {
    message = message.substring(0, 200) + '...';
  }

  return message || 'Unknown error occurred';
}

// =============================================================================
// Snapshot Collectors
// =============================================================================

/**
 * Collect workspace snapshot from workspaceStore.
 * Only collects structural info, no content.
 */
export function collectWorkspaceSnapshot(): WorkspaceSnapshot {
  try {
    if (!workspaceStoreAccessor) {
      console.warn('[CrashContext] Workspace store accessor not registered');
      return { activeTabType: null, activeTabEngine: null, tabCount: 0 };
    }

    const state = workspaceStoreAccessor();
    const activeTab = state.tabs.find((tab) => tab.id === state.activeTabId);

    // Get engine type from the active tab's connection
    let activeTabEngine: EngineType | null = null;
    if (activeTab?.connectionInfo?.connectionId && connectionStoreAccessor) {
      const connectionState = connectionStoreAccessor();
      const activeConnection = connectionState.activeConnections.find(
        (conn) => conn.id === activeTab.connectionInfo?.connectionId
      );
      activeTabEngine = asKnownEngine(activeConnection?.connection?.engine);
    }

    return {
      activeTabType: activeTab?.type ?? null,
      activeTabEngine,
      tabCount: state.tabs.length,
    };
  } catch (error) {
    // Return safe defaults if store is unavailable
    console.warn('[CrashContext] Failed to collect workspace snapshot:', error);
    return {
      activeTabType: null,
      activeTabEngine: null,
      tabCount: 0,
    };
  }
}

/**
 * Collect connection snapshot from connectionStore.
 * Only collects engine names, NEVER credentials or connection strings.
 */
export function collectConnectionSnapshot(): ConnectionSnapshot {
  try {
    if (!connectionStoreAccessor) {
      console.warn('[CrashContext] Connection store accessor not registered');
      return { connectionEngines: [] };
    }

    const state = connectionStoreAccessor();

    // Extract only engine types from active connections. Unknown engines are
    // dropped, never coerced — a past 'oracle' → 'mssql' fallback silently
    // mislabeled every Oracle event (see analyticsTypes.ts doctrine).
    const engines: EngineType[] = state.activeConnections
      .map((conn) => asKnownEngine(conn.connection?.engine))
      .filter((engine): engine is EngineType => engine !== null)
      .filter((engine, index, arr) => arr.indexOf(engine) === index); // Deduplicate

    return {
      connectionEngines: engines,
    };
  } catch (error) {
    // Return safe defaults if store is unavailable
    console.warn('[CrashContext] Failed to collect connection snapshot:', error);
    return {
      connectionEngines: [],
    };
  }
}

/**
 * Collect app info (version, platform, arch).
 * Uses electronAPI when available, falls back to browser detection.
 */
export async function collectAppInfo(): Promise<AppInfo> {
  let version = 'unknown';
  let platform: PlatformType = 'darwin';
  let arch = 'unknown';

  try {
    // Try to get version and arch from auto-update state. The state carries
    // process.arch, which is authoritative — the userAgent sniff below can't
    // see the arch on macOS (Chromium mac UAs carry no arch token).
    if (window.electronAPI?.autoUpdates?.getState) {
      const updateState = await window.electronAPI.autoUpdates.getState();
      if (updateState?.currentVersion) {
        version = updateState.currentVersion;
      }
      if (updateState?.arch) {
        arch = updateState.arch;
      }
    }

    // Try to get platform from device info
    if (window.electronAPI?.device?.getInfo) {
      const deviceInfo = await window.electronAPI.device.getInfo();
      if (deviceInfo?.platform) {
        platform = deviceInfo.platform as PlatformType;
      }
    }

    // Fallback: detect arch from userAgent when the update state didn't provide it
    if (arch === 'unknown') {
      const ua = navigator.userAgent;
      if (ua.includes('arm64') || ua.includes('aarch64')) {
        arch = 'arm64';
      } else if (ua.includes('x64') || ua.includes('x86_64') || ua.includes('amd64')) {
        arch = 'x64';
      } else if (ua.includes('x86') || ua.includes('i686')) {
        arch = 'x86';
      }
    }
  } catch {
    // Fallback to defaults on any error
  }

  return { version, platform, arch };
}

/**
 * Collect app info synchronously (best effort, may have defaults).
 * Use collectAppInfo() for full async version.
 */
export function collectAppInfoSync(): AppInfo {
  let platform: PlatformType = 'darwin';

  // Detect platform from userAgent
  const ua = navigator.userAgent;
  if (ua.includes('Windows')) {
    platform = 'win32';
  } else if (ua.includes('Linux')) {
    platform = 'linux';
  } else if (ua.includes('Mac')) {
    platform = 'darwin';
  }

  return {
    version: APP_VERSION,
    platform,
    arch: APP_ARCH,
  };
}

/**
 * Collect runtime metrics (session age, memory usage).
 */
export function collectRuntimeMetrics(): RuntimeMetrics {
  const sessionAge = Date.now() - APP_START_TIME;

  // Try to get memory usage from performance API (Chrome only)
  let memoryUsageMB = 0;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const perf = performance as any;
    if (perf.memory?.usedJSHeapSize) {
      memoryUsageMB = Math.round(perf.memory.usedJSHeapSize / (1024 * 1024));
    }
  } catch {
    // Memory API not available
  }

  return {
    sessionAge,
    memoryUsageMB,
  };
}

/**
 * Create a sanitized error object from an Error instance.
 */
export function createSanitizedError(error: unknown): SanitizedError {
  return {
    category: categorizeError(error),
    message: sanitizeErrorMessage(error),
  };
}

// =============================================================================
// Main Collector Function
// =============================================================================

/**
 * Collect complete crash context for an error.
 * All fields are privacy-safe and can be sent to analytics.
 *
 * @param error - The error that triggered the crash
 * @param source - Where the error originated ('renderer' or 'main')
 */
export async function collectCrashContext(
  error: unknown,
  source: 'renderer' | 'main' = 'renderer'
): Promise<CrashContext> {
  const [appInfo] = await Promise.all([collectAppInfo()]);

  return {
    workspace: collectWorkspaceSnapshot(),
    connection: collectConnectionSnapshot(),
    app: appInfo,
    metrics: collectRuntimeMetrics(),
    breadcrumbs: getBreadcrumbs(),
    error: createSanitizedError(error),
    source,
  };
}

/**
 * Collect crash context synchronously (for error boundaries that can't use async).
 * Uses sync versions of collectors which may have less accurate data.
 */
export function collectCrashContextSync(
  error: unknown,
  source: 'renderer' | 'main' = 'renderer'
): CrashContext {
  return {
    workspace: collectWorkspaceSnapshot(),
    connection: collectConnectionSnapshot(),
    app: collectAppInfoSync(),
    metrics: collectRuntimeMetrics(),
    breadcrumbs: getBreadcrumbs(),
    error: createSanitizedError(error),
    source,
  };
}

// =============================================================================
// Test Utilities
// =============================================================================

/**
 * Reset app start time (for testing only).
 * @internal
 */
export function _resetAppStartTime(): void {
  // This is intentionally a no-op in production
  // In tests, we can mock this module to control timing
}

/**
 * Get app start time (for testing only).
 * @internal
 */
export function _getAppStartTime(): number {
  return APP_START_TIME;
}
